/**
 * SoleadLab 2.0 — Observation List Component
 *
 * B3.2: Minimal consumption path for observation entities from backend authority.
 *
 * Displays observations for the current site in a compact card list.
 * Supports the full CRUD surface exposed by observationStore (create/update
 * actions are available via the store but this component only renders the
 * read path for minimal-consumption compliance).
 *
 * Usage:
 *   <ObservationList siteId="site-123" />
 *   <ObservationList siteId="site-123" filters={{ severity: 'high' }} />
 *
 * The component sets the site scope on the observation store,
 * fetches items on mount, and renders each observation as a card
 * showing type, severity, status, observation date, and source type.
 */

import { useEffect } from 'react';
import { useObservationStore } from '@/stores/observationStore';
import type { ObservationFilters } from '@/services/observationApi';

interface ObservationListProps {
  siteId: string;
  filters?: ObservationFilters;
}

const SEVERITY_LABELS: Record<string, string> = {
  low: 'Low',
  medium: 'Med',
  high: 'High',
  critical: 'Crit',
};

const STATUS_LABELS: Record<string, string> = {
  confirmed: 'Confirmed',
  suggested: 'Suggested',
  rejected: 'Rejected',
};

function formatDate(iso: string | null): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

export default function ObservationList({ siteId, filters }: ObservationListProps) {
  const items = useObservationStore((s) => s.items);
  const loading = useObservationStore((s) => s.loading);
  const error = useObservationStore((s) => s.error);
  const setSiteId = useObservationStore((s) => s.setSiteId);
  const fetchItems = useObservationStore((s) => s.fetchItems);

  useEffect(() => {
    setSiteId(siteId);
  }, [siteId, setSiteId]);

  useEffect(() => {
    if (siteId) {
      fetchItems(filters);
    }
    // filters object identity changes on every render — stringify to stabilize
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, JSON.stringify(filters), fetchItems]);

  if (loading) {
    return <div className="observation-list__status">Loading observations...</div>;
  }

  if (error) {
    return <div className="observation-list__status observation-list__error">{error}</div>;
  }

  if (items.length === 0) {
    return <div className="observation-list__status">No observations found.</div>;
  }

  return (
    <div className="observation-list">
      <div className="observation-list__header">
        {items.length} observation{items.length !== 1 ? 's' : ''}
      </div>
      <div className="observation-list__items">
        {items.map((obs) => (
          <div key={obs.id} className="observation-list__card">
            <div className="observation-list__card-type">
              {obs.observationType}
            </div>
            <div className="observation-list__card-meta">
              {obs.severity && (
                <span
                  className={`observation-list__severity observation-list__severity--${obs.severity}`}
                >
                  {SEVERITY_LABELS[obs.severity] ?? obs.severity}
                </span>
              )}
              {obs.status && (
                <span className="observation-list__status-badge">
                  {STATUS_LABELS[obs.status] ?? obs.status}
                </span>
              )}
              {obs.createdAt && (
                <span className="observation-list__date">
                  {formatDate(obs.createdAt)}
                </span>
              )}
            </div>
            <div className="observation-list__card-source">
              {obs.sourceType}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
